import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function EditBarModal({ open, onClose, bar, bars = [], onUpdate, serverCurrentBar, onCurrentBarChange, onAdd, onBarsReorder }) {
  const [tabValue, setTabValue] = useState(0);
  const [editedBar, setEditedBar] = useState(bar);
  const [saving, setSaving] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [downloadingImage, setDownloadingImage] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isCurrentBar, setIsCurrentBar] = useState(false);
  
  // Add bar state
  const [newBar, setNewBar] = useState({
    name: '',
    address: '',
    par: 3,
    drink: 'Beer',
  });
  
  // Bar sort state
  const [sortedBars, setSortedBars] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (bar) {
      setTabValue(0); // Switch to edit tab when bar is provided
      setEditedBar(bar);
      setBackgroundImageUrl('');
      // Initialize position from bar data or default to center
      setImagePosition({
        x: bar.background_position_x !== null && bar.background_position_x !== undefined ? bar.background_position_x : 50,
        y: bar.background_position_y !== null && bar.background_position_y !== undefined ? bar.background_position_y : 50,
      });
      // Check if this bar is the current bar
      setIsCurrentBar(serverCurrentBar && serverCurrentBar.id === bar.id);
    } else {
      setTabValue(1); // Switch to add tab (index 1 when no edit tab exists, since Edit is tab 0)
    }
    // Initialize sorted bars whenever bars prop changes or modal opens
    if (bars && bars.length > 0) {
      const sorted = [...bars].sort((a, b) => (a.order || 0) - (b.order || 0));
      setSortedBars(sorted);
    } else {
      setSortedBars([]);
    }
  }, [bar, serverCurrentBar, bars, open]);

  const handleFieldChange = (field, value) => {
    if (!editedBar) return;
    setEditedBar({ ...editedBar, [field]: value });
  };

  const handleScoreChange = (index, newScore) => {
    if (!editedBar) return;
    const updatedScores = [...editedBar.scores];
    updatedScores[index].score = parseInt(newScore) || 1;
    setEditedBar({ ...editedBar, scores: updatedScores });
  };

  const handleDeleteScore = (index) => {
    if (!editedBar) return;
    if (window.confirm(`Delete score for ${editedBar.scores[index].name}?`)) {
      const updatedScores = editedBar.scores.filter((_, i) => i !== index);
      setEditedBar({ ...editedBar, scores: updatedScores });
    }
  };

  const handleSave = async () => {
    if (!editedBar || !bar) return;
    setSaving(true);
    try {
      let backgroundImage = editedBar.background_image;
      
      // If a background image URL is provided, download and compress it
      if (backgroundImageUrl && backgroundImageUrl.trim()) {
        setDownloadingImage(true);
        try {
          const response = await axios.post(
            `${API_URL}/api/download-image`,
            { url: backgroundImageUrl.trim() },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          backgroundImage = response.data.url;
        } catch (error) {
          console.error('Error downloading image:', error);
          alert('Failed to download image. Please check the URL and try again.');
          setSaving(false);
          setDownloadingImage(false);
          return;
        } finally {
          setDownloadingImage(false);
        }
      }
      
      // Update bar info
      const barUpdate = {
        name: editedBar.name,
        address: editedBar.address,
        par: parseInt(editedBar.par),
        drink: editedBar.drink,
        background_image: backgroundImage,
        background_position_x: imagePosition.x,
        background_position_y: imagePosition.y,
      };
      await axios.put(`${API_URL}/api/bars/${bar.id}`, barUpdate, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Handle score updates and deletions
      // First, update the entire scores array to handle both updates and deletions
      await axios.put(`${API_URL}/api/bars/${bar.id}/scores`, {
        scores: editedBar.scores,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Handle current bar toggle - only update if checkbox state changed
      if (onCurrentBarChange) {
        const wasCurrentBar = serverCurrentBar && serverCurrentBar.id === bar.id;
        if (isCurrentBar !== wasCurrentBar) {
          try {
            if (isCurrentBar) {
              // Set as current bar
              await axios.put(`${API_URL}/api/current-bar`, { bar_id: bar.id }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
              });
              const fullBar = await axios.get(`${API_URL}/api/bars/${bar.id}`);
              if (onCurrentBarChange) {
                onCurrentBarChange(fullBar.data);
              }
            } else {
              // Unset current bar (set to null/empty)
              await axios.put(`${API_URL}/api/current-bar`, { bar_id: null }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
              });
              if (onCurrentBarChange) {
                onCurrentBarChange(null);
              }
            }
          } catch (error) {
            console.error('Error updating current bar:', error);
            alert('Failed to update current bar. Please try again.');
          }
        }
      }

      // Refresh bar data
      const response = await axios.get(`${API_URL}/api/bars/${bar.id}`);
      if (onUpdate) {
        onUpdate(response.data);
      }
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBar = async () => {
    // Validate required fields
    if (!newBar.name.trim()) {
      alert('Bar name is required');
      return;
    }
    if (!newBar.address.trim()) {
      alert('Bar address is required');
      return;
    }
    if (!newBar.par || newBar.par < 1) {
      alert('Par must be at least 1');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/bars`, {
        name: newBar.name.trim(),
        address: newBar.address.trim(),
        par: parseInt(newBar.par),
        drink: newBar.drink.trim() || 'Beer',
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (onAdd) {
        onAdd(response.data);
      }
      
      // Reset form
      setNewBar({
        name: '',
        address: '',
        par: 3,
        drink: 'Beer',
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating bar:', error);
      alert('Failed to create bar. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewBarFieldChange = (field, value) => {
    setNewBar({ ...newBar, [field]: value });
  };

  // Bar sort handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newSortedBars = [...sortedBars];
    const draggedBar = newSortedBars[draggedIndex];
    newSortedBars.splice(draggedIndex, 1);
    newSortedBars.splice(index, 0, draggedBar);
    setSortedBars(newSortedBars);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveBarOrder = async () => {
    if (!onBarsReorder) return;
    
    setSaving(true);
    try {
      const barOrders = sortedBars.map((bar, index) => ({
        bar_id: bar.id,
        order: index,
      }));
      
      await onBarsReorder(barOrders);
      onClose();
    } catch (error) {
      console.error('Error saving bar order:', error);
      alert('Failed to save bar order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {bar ? `Edit Bar: ${bar.name}` : 'Add New Bar'}
      </DialogTitle>
      <Tabs 
        value={bar ? tabValue : (tabValue === 0 ? 0 : tabValue - 1)} 
        onChange={(e, newValue) => setTabValue(bar ? newValue : newValue + 1)} 
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {bar && <Tab label="Edit Bar" />}
        <Tab label="Add Bar" />
        <Tab label="Bar Sort" />
      </Tabs>
      <DialogContent>
        {bar && tabValue === 0 && editedBar && (
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Bar Information
              </Typography>
            <TextField
              fullWidth
              label="Bar Name"
              value={editedBar.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Address"
              value={editedBar.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              margin="normal"
              variant="outlined"
            />
            <Box display="flex" gap={2} mt={2}>
              <TextField
                label="Par"
                type="number"
                value={editedBar.par || ''}
                onChange={(e) => handleFieldChange('par', e.target.value)}
                variant="outlined"
                sx={{ width: '150px' }}
              />
              <TextField
                fullWidth
                label="Drink"
                value={editedBar.drink || ''}
                onChange={(e) => handleFieldChange('drink', e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCurrentBar}
                    onChange={(e) => setIsCurrentBar(e.target.checked)}
                    color="primary"
                  />
                }
                label="Set as Current Bar"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Only one bar can be the current bar at a time. Checking this will unset any other current bar.
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Background Image URL"
              value={backgroundImageUrl}
              onChange={(e) => setBackgroundImageUrl(e.target.value)}
              margin="normal"
              variant="outlined"
              placeholder="https://example.com/image.jpg"
              helperText="Enter a URL to download and compress the image. Leave empty to keep current image."
            />
            {editedBar.background_image && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Background Image Position
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Drag the image to adjust its position. The clear area shows what will be visible on the bar card.
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '400px',
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: '#f5f5f5',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                  }}
                  onMouseDown={(e) => {
                    if (!editedBar.background_image) return;
                    setIsDragging(true);
                    const container = e.currentTarget.getBoundingClientRect();
                    setDragStart({
                      x: e.clientX - (container.left + (imagePosition.x / 100) * container.width),
                      y: e.clientY - (container.top + (imagePosition.y / 100) * container.height),
                    });
                  }}
                  onMouseMove={(e) => {
                    if (!isDragging || !editedBar.background_image) return;
                    e.preventDefault();
                    const container = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - dragStart.x - container.left) / container.width) * 100;
                    const y = ((e.clientY - dragStart.y - container.top) / container.height) * 100;
                    setImagePosition({
                      x: Math.max(0, Math.min(100, x)),
                      y: Math.max(0, Math.min(100, y)),
                    });
                  }}
                  onMouseUp={() => {
                    setIsDragging(false);
                  }}
                  onMouseLeave={() => {
                    setIsDragging(false);
                  }}
                >
                  {/* Full background image */}
                  <Box
                    component="img"
                    src={`${API_URL}${editedBar.background_image}`}
                    alt="Background"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                      pointerEvents: 'none',
                    }}
                  />
                  
                  {/* Gray overlay for areas outside viewport */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(128, 128, 128, 0.8)',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                  
                  {/* Clear viewport area (representing the card) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '20%',
                      left: '10%',
                      width: '80%',
                      height: '60%',
                      border: '3px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                      zIndex: 2,
                      boxShadow: '0 0 0 9999px rgba(128, 128, 128, 0.8)',
                    }}
                  />
                  
                  {/* Position indicator */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: `${imagePosition.y}%`,
                      left: `${imagePosition.x}%`,
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      border: '2px solid white',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      zIndex: 3,
                      boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
                    }}
                  />
                </Box>
                <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    X: {imagePosition.x.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Y: {imagePosition.y.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Scores ({editedBar.scores?.length || 0})
            </Typography>
            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {editedBar.scores?.map((score, index) => (
                <ListItem
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={score.avatar}
                      alt={score.name}
                      sx={{ border: '2px solid #667eea' }}
                    >
                      {score.name?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={score.name}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <TextField
                    type="number"
                    value={score.score}
                    onChange={(e) => handleScoreChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ width: '100px', mr: 1 }}
                    inputProps={{ min: 1 }}
                  />
                  <IconButton
                    onClick={() => handleDeleteScore(index)}
                    color="error"
                    size="small"
                    title="Delete Score"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
        )}
        
        {((bar && tabValue === 1) || !bar) && (
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Bar Information
              </Typography>
              <TextField
                fullWidth
                label="Bar Name"
                value={newBar.name}
                onChange={(e) => handleNewBarFieldChange('name', e.target.value)}
                margin="normal"
                variant="outlined"
                required
              />
              <TextField
                fullWidth
                label="Address"
                value={newBar.address}
                onChange={(e) => handleNewBarFieldChange('address', e.target.value)}
                margin="normal"
                variant="outlined"
                required
              />
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  label="Par"
                  type="number"
                  value={newBar.par}
                  onChange={(e) => handleNewBarFieldChange('par', e.target.value)}
                  variant="outlined"
                  sx={{ width: '150px' }}
                  inputProps={{ min: 1 }}
                  required
                />
                <TextField
                  fullWidth
                  label="Drink"
                  value={newBar.drink}
                  onChange={(e) => handleNewBarFieldChange('drink', e.target.value)}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>
        )}
        
        {((bar && tabValue === 2) || (!bar && tabValue === 2)) && (
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Drag and drop bars to reorder them
            </Typography>
            <List>
              {sortedBars.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No bars available to sort.
                </Typography>
              ) : (
                sortedBars.map((barItem, index) => (
                <ListItem
                  key={barItem.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    backgroundColor: draggedIndex === index ? 'action.selected' : index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'move',
                    border: draggedIndex === index ? '2px dashed' : '2px solid transparent',
                    borderColor: draggedIndex === index ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={`${index + 1}. ${barItem.name}`}
                    secondary={barItem.address}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    Par {barItem.par} • {barItem.drink}
                  </Typography>
                </ListItem>
                ))
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {bar && tabValue === 0 ? (
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || downloadingImage}
          >
            {downloadingImage ? 'Downloading image...' : saving ? 'Saving...' : 'Save Changes'}
          </Button>
        ) : ((bar && tabValue === 2) || (!bar && tabValue === 2)) ? (
          <Button
            onClick={handleSaveBarOrder}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        ) : (
          <Button
            onClick={handleAddBar}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create Bar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default EditBarModal;

